import { UserFormData } from 'redux/actions/admin'
import { IConfigRetailUser } from 'constants/interfaces'

/**
 * Sanitizes services_permissions based on main permission level and operation type
 * @param formData - Form data from the user form
 * @param isCreate - Whether this is a create operation
 * @param originalUser - Original user data (for edit operations)
 * @returns sanitized form data
 */
export const sanitizeUserFormData = (
  formData: UserFormData,
  isCreate: boolean,
  originalUser?: IConfigRetailUser | null
): UserFormData => {
  const sanitizedData = { ...formData }

  // If no services_permissions, return as is
  if (!sanitizedData.services_permissions) {
    return sanitizedData
  }

  const mainPermission = sanitizedData.permission
  const allValuesMatchMainPermission = Object.values(sanitizedData.services_permissions).every(
    (value) => value === mainPermission
  )

  if (isCreate) {
    // For create operations: if all values match main permission, remove services_permissions
    if (allValuesMatchMainPermission) {
      delete sanitizedData.services_permissions
    }
    return sanitizedData
  }

  // For edit operations
  if (!originalUser || !originalUser.services_permissions) {
    // If original user doesn't have services_permissions, treat as create
    if (allValuesMatchMainPermission) {
      delete sanitizedData.services_permissions
    }
    return sanitizedData
  }

  // If current data has all values matching main permission
  if (allValuesMatchMainPermission) {
    // Check if original user had any different values
    const originalHadDifferentValues = Object.values(originalUser.services_permissions).some(
      (value) => value !== mainPermission
    )

    // If original had different values but now all match main permission,
    // we need to send the data to update it on server
    if (!originalHadDifferentValues) {
      delete sanitizedData.services_permissions
    }
  }

  // If current data has non-main permission values, always send
  return sanitizedData
}
